import type { FeedbackContext, FeedbackFormConfiguration } from './types'
import { FeedbackStrings, type FeedbackStringSet } from './strings'

/**
 * Builds the {@link FeedbackFormConfiguration} for a context. 1:1 port of
 * iOS `FeedbackConfigurationFactory` — same titles, icons, categories,
 * reason order, and footer presence per context.
 *
 * Reason / category ids are STABLE string keys (not random UUIDs like
 * iOS, which regenerates them per build) so selection + the analytics
 * payload are deterministic and testable. The analytics payload joins
 * reason TITLES (not ids), matching iOS.
 */
export function buildConfiguration(
  context: FeedbackContext,
  strings: FeedbackStringSet = FeedbackStrings,
): FeedbackFormConfiguration {
  switch (context) {
    case 'route':
      return _routeConfiguration(strings)
    case 'routeDetails':
      return _routeDetailsConfiguration(strings)
    case 'stationDetails':
      return _stationDetailsConfiguration(strings)
  }
}

function _routeConfiguration(strings: FeedbackStringSet): FeedbackFormConfiguration {
  const s = strings.route
  return {
    title: s.title,
    iconName: 'thumbDownRed',
    categories: [
      {
        id: 'route-accuracy',
        title: s.categories.routeAccuracy,
        reasons: [
          { id: 'wrong-route', title: s.reasons.wrongRoute },
          { id: 'too-many-transfers', title: s.reasons.tooManyTransfers },
          { id: 'timing-was-wrong', title: s.reasons.timingWasWrong },
          { id: 'wrong-direction', title: s.reasons.wrongDirection },
        ],
      },
      {
        id: 'map-and-navigation',
        title: s.categories.mapAndNavigation,
        reasons: [
          { id: 'hard-to-use', title: s.reasons.hardToUse },
          { id: 'hard-to-find-stations', title: s.reasons.hardToFindStations },
          { id: 'incorrect-map-info', title: s.reasons.incorrectMapInfo },
        ],
      },
      {
        id: 'app-experience',
        title: s.categories.appExperience,
        reasons: [
          { id: 'slow-or-buggy', title: s.reasons.slowOrBuggy },
          { id: 'confusing-interface', title: s.reasons.confusingInterface },
        ],
      },
    ],
    footerText: s.footer,
    submitTitle: strings.submit,
  }
}

function _routeDetailsConfiguration(strings: FeedbackStringSet): FeedbackFormConfiguration {
  const s = strings.routeDetails
  return {
    title: s.title,
    iconName: 'thumbDownBlack',
    categories: [
      {
        id: 'route-accuracy',
        title: s.categories.routeAccuracy,
        reasons: [
          { id: 'wrong-route', title: s.reasons.wrongRoute },
          { id: 'too-many-transfers', title: s.reasons.tooManyTransfers },
          { id: 'timing-was-wrong', title: s.reasons.timingWasWrong },
          { id: 'wrong-direction', title: s.reasons.wrongDirection },
        ],
      },
      {
        id: 'map-and-navigation',
        title: s.categories.mapAndNavigation,
        reasons: [
          { id: 'hard-to-use', title: s.reasons.hardToUse },
          { id: 'hard-to-find-stations', title: s.reasons.hardToFindStations },
          { id: 'incorrect-map-info', title: s.reasons.incorrectMapInfo },
        ],
      },
      {
        id: 'app-experience',
        title: s.categories.appExperience,
        reasons: [
          { id: 'slow-or-buggy', title: s.reasons.slowOrBuggy },
          { id: 'confusing-interface', title: s.reasons.confusingInterface },
        ],
      },
    ],
    // No footer for routeDetails (mirrors iOS `footerText: nil`).
    submitTitle: strings.submit,
  }
}

function _stationDetailsConfiguration(strings: FeedbackStringSet): FeedbackFormConfiguration {
  const s = strings.stationDetails
  return {
    title: s.title,
    iconName: 'thumbDownBlack',
    categories: [
      {
        id: 'station-info',
        title: s.categories.stationInfo,
        reasons: [
          { id: 'incorrect-info', title: s.reasons.incorrectInfo },
          { id: 'missing-info', title: s.reasons.missingInfo },
          { id: 'wrong-location', title: s.reasons.wrongLocation },
        ],
      },
      {
        id: 'map-and-navigation',
        title: s.categories.mapAndNavigation,
        reasons: [
          { id: 'hard-to-find', title: s.reasons.hardToFind },
          { id: 'incorrect-map-info', title: s.reasons.incorrectMapInfo },
        ],
      },
      {
        id: 'app-experience',
        title: s.categories.appExperience,
        reasons: [
          { id: 'slow-or-buggy', title: s.reasons.slowOrBuggy },
          { id: 'confusing-interface', title: s.reasons.confusingInterface },
        ],
      },
    ],
    // No footer for stationDetails (mirrors iOS `footerText: nil`).
    submitTitle: strings.submit,
  }
}
